import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {Animation | null} */
  #menuDrawerAnimation = null;

  /** @type {'opening' | 'closing' | null} */
  #menuDrawerAnimationDirection = null;

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    this.#menuDrawerAnimation?.cancel();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // The mobile header menu needs a real closing phase. Native <details>
    // hides its contents immediately, which prevents the upward exit motion.
    if (isMobile && this.closest('.menu-drawer')) {
      event.preventDefault();
      this.#toggleMenuDrawerAccordion();
    }
  };

  /**
   * Animate a mobile menu accordion in both directions while preserving the
   * existing <details>/<summary> semantics and aria state.
   */
  #toggleMenuDrawerAccordion() {
    const { details, summary } = this;
    const content = details.querySelector(':scope > .details-content');

    if (!(content instanceof HTMLElement)) {
      details.open = !details.open;
      summary.setAttribute('aria-expanded', details.open.toString());
      return;
    }

    // The generic accordion stylesheet also transitions block-size and
    // opacity. Disable that transition for the menu drawer so this Web
    // Animation is the only owner of the Collections motion.
    content.style.transition = 'none';

    const wasClosed = !details.open;
    const shouldOpen = wasClosed || this.#menuDrawerAnimationDirection === 'closing';
    const interruptedStyles = getComputedStyle(content);
    const interruptedHeight = content.getBoundingClientRect().height;

    this.#menuDrawerAnimation?.cancel();

    if (shouldOpen) {
      details.open = true;
    }

    summary.setAttribute('aria-expanded', shouldOpen.toString());

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      details.open = shouldOpen;
      this.#menuDrawerAnimation = null;
      this.#menuDrawerAnimationDirection = null;
      return;
    }

    const currentHeight = wasClosed ? 0 : interruptedHeight;
    const currentOpacityValue = Number.parseFloat(interruptedStyles.opacity);
    const currentOpacity = wasClosed || !Number.isFinite(currentOpacityValue) ? 0 : currentOpacityValue;
    const currentTransform =
      wasClosed || interruptedStyles.transform === 'none'
        ? 'translate3d(0, -18px, 0)'
        : interruptedStyles.transform;
    const openHeight = content.scrollHeight;
    // Keep one mobile-only motion profile in both directions. The shorter
    // duration removes the heavy pause while the eased finish keeps the
    // accordion controlled instead of snapping into place.
    const duration = 300;
    const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

    this.#menuDrawerAnimationDirection = shouldOpen ? 'opening' : 'closing';
    const animation = content.animate(
      shouldOpen
        ? [
            {
              height: `${currentHeight}px`,
              opacity: currentOpacity,
              transform: currentTransform,
            },
            {
              height: `${openHeight}px`,
              opacity: 1,
              transform: 'translate3d(0, 0, 0)',
            },
          ]
        : [
            {
              height: `${currentHeight}px`,
              opacity: currentOpacity,
              transform:
                interruptedStyles.transform === 'none' ? 'translate3d(0, 0, 0)' : interruptedStyles.transform,
            },
            {
              height: '0px',
              opacity: 0,
              transform: 'translate3d(0, -18px, 0)',
            },
      ],
      {
        duration,
        easing,
        fill: 'both',
      }
    );

    this.#menuDrawerAnimation = animation;

    animation.finished
      .then(() => {
        if (this.#menuDrawerAnimation !== animation) return;

        if (!shouldOpen) {
          details.open = false;
        }

        animation.cancel();
        this.#menuDrawerAnimation = null;
        this.#menuDrawerAnimationDirection = null;
      })
      .catch(() => {
        // Cancellation is expected when the user reverses the animation.
      });
  }

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
